import { action, makeObservable, observable } from "mobx"

export const SCRATCHPADS_NAMESPACE = "wildlark.scratchpads"

export interface ScratchpadMeta {
    id: string
    title: string
    preview: string
    createdAt: string
    updatedAt: string
}

export interface ScratchpadContent {
    content: Record<string, unknown> | null
    plainText: string
}

export interface ScratchpadIndexRecord {
    v: 1
    workspaceId: string
    pads: ScratchpadMeta[]
    updatedAt: string
}

export interface ScratchpadContentRecord {
    v: 1
    workspaceId: string
    padId: string
    content: Record<string, unknown> | null
    plainText: string
    updatedAt: string
}

export interface ScratchpadArrayHandle<T extends { id: string }> {
    all(): T[]
    get(id: string): T | undefined
    push(item: T): void
    update(id: string, recipe: (draft: T) => void): void
    delete(id: string): void
}

export interface ScratchpadObjectHandle<T> {
    current: T
    get(): T
    set(partial: Partial<T>): void
}

export interface ScratchpadIndexStore {
    pads: ScratchpadArrayHandle<ScratchpadMeta>
}

export interface ScratchpadContentStore {
    data: ScratchpadObjectHandle<ScratchpadContent>
}

export function scratchpadIndexKey(workspaceId: string): string {
    return `index.${workspaceId}`
}

export function scratchpadContentKey(padId: string): string {
    return `pad.${padId}`
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function stringField(record: Record<string, unknown>, key: string): string | null {
    const value = record[key]
    return typeof value === "string" && value ? value : null
}

function parseScratchpadMeta(value: unknown): ScratchpadMeta | null {
    if (!isRecord(value)) return null
    const id = stringField(value, "id")
    const title = stringField(value, "title")
    const createdAt = stringField(value, "createdAt")
    const updatedAt = stringField(value, "updatedAt")
    if (!id || !title || !createdAt || !updatedAt) return null
    return {
        id,
        title,
        preview: typeof value.preview === "string" ? value.preview : "",
        createdAt,
        updatedAt,
    }
}

function parseContent(value: unknown): Record<string, unknown> | null {
    return isRecord(value) ? value : null
}

export function parseScratchpadIndexRecord(value: unknown): ScratchpadIndexRecord | null {
    if (!isRecord(value) || value.v !== 1) return null
    const workspaceId = stringField(value, "workspaceId")
    const updatedAt = stringField(value, "updatedAt")
    if (!workspaceId || !updatedAt || !Array.isArray(value.pads)) return null
    return {
        v: 1,
        workspaceId,
        pads: value.pads.map(parseScratchpadMeta).filter((pad): pad is ScratchpadMeta => pad !== null),
        updatedAt,
    }
}

export function parseScratchpadContentRecord(value: unknown): ScratchpadContentRecord | null {
    if (!isRecord(value) || value.v !== 1) return null
    const workspaceId = stringField(value, "workspaceId")
    const padId = stringField(value, "padId")
    const updatedAt = stringField(value, "updatedAt")
    if (!workspaceId || !padId || !updatedAt) return null
    return {
        v: 1,
        workspaceId,
        padId,
        content: parseContent(value.content),
        plainText: typeof value.plainText === "string" ? value.plainText : "",
        updatedAt,
    }
}

function clone<T>(value: T): T {
    return globalThis.structuredClone(value)
}

export function createScratchpadIndexRecord(workspaceId: string, pads: ScratchpadMeta[] = [], updatedAt = new Date().toISOString()): ScratchpadIndexRecord {
    return {
        v: 1,
        workspaceId,
        pads,
        updatedAt,
    }
}

export function createScratchpadContentRecord(
    workspaceId: string,
    padId: string,
    content: ScratchpadContent = { content: null, plainText: "" },
    updatedAt = new Date().toISOString()
): ScratchpadContentRecord {
    return {
        v: 1,
        workspaceId,
        padId,
        content: content.content,
        plainText: content.plainText,
        updatedAt,
    }
}

export function createScratchpadIndexStore(record: ScratchpadIndexRecord, onChange?: (pads: ScratchpadMeta[]) => void): ScratchpadIndexStore {
    const pads = observable.array<ScratchpadMeta>(record.pads, { deep: false })
    const notify = () => onChange?.(pads.map((pad) => clone(pad)))

    const handle: ScratchpadArrayHandle<ScratchpadMeta> = {
        all(): ScratchpadMeta[] {
            return pads.map((pad) => clone(pad))
        },

        get(id: string): ScratchpadMeta | undefined {
            const pad = pads.find((item) => item.id === id)
            return pad ? clone(pad) : undefined
        },

        push(item: ScratchpadMeta): void {
            pads.push(clone(item))
            notify()
        },

        update(id: string, recipe: (draft: ScratchpadMeta) => void): void {
            const index = pads.findIndex((item) => item.id === id)
            if (index === -1) return
            const draft = clone(pads[index])
            recipe(draft)
            pads[index] = draft
            notify()
        },

        delete(id: string): void {
            const index = pads.findIndex((item) => item.id === id)
            if (index === -1) return
            pads.splice(index, 1)
            notify()
        },
    }

    makeObservable(handle, {
        push: action,
        update: action,
        delete: action,
    })

    return { pads: handle }
}

export function createScratchpadContentStore(record: ScratchpadContentRecord, onChange?: (content: ScratchpadContent) => void): ScratchpadContentStore {
    const handle: ScratchpadObjectHandle<ScratchpadContent> = {
        current: {
            content: record.content,
            plainText: record.plainText,
        },

        get(): ScratchpadContent {
            return clone(this.current)
        },

        set(partial: Partial<ScratchpadContent>): void {
            this.current = {
                ...this.current,
                ...partial,
            }
            onChange?.(this.get())
        },
    }

    makeObservable(handle, {
        current: observable.ref,
        set: action,
    })

    return { data: handle }
}
